import { useTranslation } from 'react-i18next';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { EllipsisHorizontalIcon as EllipsisHorizontalIconSolid } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useNotifications } from '../../contexts/NotificationContext';
import { primaryNavItems, getActiveTabId, MORE_TAB_ID } from './navItems';

interface BottomTabBarProps {
  sheetOpen: boolean;
  onToggleSheet: () => void;
  /**
   * Aangeroepen bij een tik op een van de vier tabs, ook als die naar de pagina wijst waar
   * je al staat. `MobileNav` sluit het Meer-paneel bij een routewissel, en dát is precies
   * het geval dat daar niet onder valt: sta je op het dashboard, open je Meer en tik je
   * Home, dan verandert het pad niet en bleef het paneel openstaan - de knop leek stuk.
   * De rijen ín het paneel sluiten al op dezelfde manier bij een tik.
   */
  onNavigate: () => void;
  moreButtonRef: React.RefObject<HTMLButtonElement | null>;
  /** Waar tijdens het naar beneden scrollen; dan wijkt de balk terug. Zie useNavKrimp. */
  gekrompen: boolean;
}

// Iconen zonder label: de namen staan in aria-label, zodat schermlezers en
// spraakbediening ze nog steeds kunnen benoemen.
const tabClasses =
  'relative z-10 flex flex-1 items-center justify-center min-h-[52px] rounded-[20px] px-1 ' +
  'transition-colors duration-200 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hockey-blue focus-visible:ring-offset-1 ' +
  'motion-reduce:transition-none';

interface PillRect {
  left: number;
  width: number;
}

export const BottomTabBar: React.FC<BottomTabBarProps> = ({
  sheetOpen,
  onToggleSheet,
  onNavigate,
  moreButtonRef,
  gekrompen,
}) => {
  const { t } = useTranslation(['nav', 'common']);
  const { pathname } = useLocation();
  const { unreadCount } = useNotifications();

  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef(new Map<string, HTMLElement>());
  const [pill, setPill] = useState<PillRect | null>(null);
  // Pas ná de eerste meting animeren, anders schuift de pil bij het laden
  // zichtbaar vanaf links het scherm in.
  const [hasMeasured, setHasMeasured] = useState(false);

  const activeTabId = getActiveTabId(pathname);
  // Het Meer-item licht ook op zolang het paneel openstaat, ook al is de
  // onderliggende pagina een van de vier hoofdtabs.
  const moreActive = sheetOpen || activeTabId === MORE_TAB_ID;
  const litTabId = moreActive ? MORE_TAB_ID : activeTabId;

  const registerTab = useCallback(
    (id: string) => (node: HTMLElement | null) => {
      if (node) {
        tabRefs.current.set(id, node);
      } else {
        tabRefs.current.delete(id);
      }
    },
    []
  );

  // De pil volgt de actieve cel op gemeten posities in plaats van op vaste
  // percentages: zo blijft hij kloppen bij een andere schermbreedte of als er
  // ooit een tab bij komt.
  useLayoutEffect(() => {
    const measure = () => {
      const nav = navRef.current;
      const tab = tabRefs.current.get(litTabId);
      if (!nav || !tab) return;

      const navBox = nav.getBoundingClientRect();
      const tabBox = tab.getBoundingClientRect();

      // getBoundingClientRect geeft schermpixels en telt de krimp-schaal van de balk
      // dus mee, terwijl de pil zijn translateX in de niet-geschaalde ruimte binnen die
      // balk krijgt. Zonder terugrekenen wordt de krimp twee keer toegepast en valt de
      // pil te smal en te ver naar links uit. offsetWidth is de breedte vóór transform,
      // dus de verhouding is de schaal die op dit moment echt getekend wordt - ook
      // halverwege de overgang, wanneer de meting niet op 1 of op de eindwaarde staat.
      const schaal = nav.offsetWidth > 0 ? navBox.width / nav.offsetWidth : 1;

      setPill({ left: (tabBox.left - navBox.left) / schaal, width: tabBox.width / schaal });
      setHasMeasured(true);
    };

    measure();

    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [litTabId]);

  return (
    <nav
      ref={navRef}
      aria-label={t('mainNav')}
      className={clsx(
        'fixed md:hidden z-50 left-3 right-3 bottom-nav-offset glass-surface rounded-[26px]',
        'ring-1 ring-black/5 dark:ring-white/10 px-1.5 py-1 flex items-stretch gap-0.5',
        // Krimpen via een enkele transform en niet via hoogte, breedte en icoongrootte:
        // dit loopt op de compositor, dus het blijft vloeiend terwijl er tegelijk
        // gescrold wordt, en de balk bestaat alleen uit pictogrammen - die schalen zonder
        // dat er tekst onscherp van wordt.
        //
        // origin-bottom houdt de onderrand op zijn plek, zodat de balk terugwijkt in het
        // scherm in plaats van naar het midden toe te kruipen.
        //
        // 0.90 en niet minder: de tabcellen zijn 52px hoog, en daaronder zakt het
        // raakvlak onder de 44px die een vinger nodig heeft.
        //
        // Ruimer bemeten dan de 380ms van de pil hieronder, en met een zachtere curve.
        // De pil moet een tik geven bij een tap; dit gebeurt ongevraagd tijdens het
        // scrollen en mag daarom niet de aandacht opeisen. De snelle curve van de pil
        // (0.32,0.72,0,1) legt bijna de hele beweging in het eerste kwart en leest hier
        // als een sprongetje; deze verdeelt het gelijkmatiger en komt rustig tot stilstand.
        'origin-bottom transition-transform duration-500 [transition-timing-function:cubic-bezier(0.33,1,0.68,1)] motion-reduce:transition-none',
        gekrompen && 'scale-[0.90]'
      )}
    >
      {/* Eén pil die meeschuift, in plaats van vijf losse achtergronden die
          aan- en uitfloepen. aria-hidden: puur decoratief, de actieve staat
          wordt al via aria-current gecommuniceerd. */}
      {pill && (
        <span
          aria-hidden="true"
          className={clsx(
            'absolute top-1 bottom-1 left-0 rounded-[20px] bg-hockey-blue shadow-[0_1px_3px_rgba(16,24,40,0.16)]',
            hasMeasured &&
              'transition-[transform,width] duration-[380ms] [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none'
          )}
          style={{ width: `${pill.width}px`, transform: `translateX(${pill.left}px)` }}
        />
      )}

      {primaryNavItems.map((item) => {
        const isActive = litTabId === item.id;
        const Icon = isActive ? item.iconSolid : item.icon;

        return (
          <Link
            key={item.id}
            ref={registerTab(item.id)}
            to={item.href}
            onClick={onNavigate}
            aria-label={t(`nav:${item.id}`)}
            aria-current={activeTabId === item.id ? 'page' : undefined}
            className={clsx(tabClasses, isActive ? 'text-white' : 'text-gray-600')}
          >
            <Icon className="h-6 w-6 shrink-0" aria-hidden="true" />
          </Link>
        );
      })}

      <button
        ref={(node) => {
          registerTab(MORE_TAB_ID)(node);
          moreButtonRef.current = node;
        }}
        type="button"
        onClick={onToggleSheet}
        aria-label={t(MORE_TAB_ID)}
        aria-expanded={sheetOpen}
        aria-haspopup="dialog"
        aria-controls="mobile-more-sheet"
        className={clsx(tabClasses, moreActive ? 'text-white' : 'text-gray-600')}
      >
        <span className="relative">
          {moreActive ? (
            <EllipsisHorizontalIconSolid className="h-6 w-6 shrink-0" aria-hidden="true" />
          ) : (
            <EllipsisHorizontalIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
          )}
          {unreadCount > 0 && (
            <span
              aria-label={`${unreadCount} ongelezen meldingen`}
              className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[19px] h-[19px] flex items-center justify-center px-1 border border-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
      </button>
    </nav>
  );
};
